import {
  Bathtub,
  Buildings,
  Compass,
  Cube,
  Drop,
  Elevator,
  Lightning,
  PaintRoller,
  Question,
  Snowflake,
  Tag,
  ThermometerCold,
  Wrench,
  X,
} from '@phosphor-icons/react';
import { TicketLabel } from 'shared/types/helpdesk';
import { getLabelIconAndColor } from 'screens/helpdesk/shared/helpdeskCellFactory';

/**
 * Kleur + icoon voor een **renovatiecategorie**, in dezelfde visuele taal als het
 * ticketlabel van de helpdesk.
 *
 * Een renovatiecategorie bleef vroeger één vlak moss-tintje, ongeacht welke categorie
 * het was: in de lijst zag "Verwarming" er identiek uit als "Keuken", terwijl dezelfde
 * "Verwarming" in de helpdesk oranje is. Categorieën die in beide modules bestaan,
 * krijgen nu **letterlijk dezelfde kleur** — de helpdeskmap is de bron.
 *
 * Waarom niet gewoon één map delen: helpdesklabels zijn een gesloten enum
 * ({@link TicketLabel}), renovatiecategorieën zijn vrije namen uit de catalogus
 * `renovation_label` (nieuwe komen erbij zonder codewijziging). De koppeling gebeurt
 * daarom in drie stappen:
 *
 * 1. **naam matcht een helpdesklabel** (genormaliseerd: kleine letters, zonder accenten
 *    en leestekens) ⇒ exact de kleur en het icoon van de helpdesk;
 * 2. **bekende schrijfwijze** uit {@link SYNONIEMEN} — "Dakwerken" is het "Dak" van de
 *    helpdesk, "Electriciteit" (zonder k) is "Elektriciteit" ⇒ idem;
 * 3. **geen tegenhanger** (Keuken, Gyprocwerken, Studiewerk, …) ⇒ een vaste kleur uit
 *    hetzelfde palet, gekozen op de naam. Dezelfde categorie krijgt dus altijd dezelfde
 *    kleur, ook na een herlaadbeurt of in een andere weergave — een willekeurige kleur
 *    per render zou de lijst onleesbaar maken.
 */

export interface RenoCategoryStyle {
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
}

/**
 * Naam terugbrengen tot iets vergelijkbaars: kleine letters, zonder accenten, spaties,
 * koppeltekens en leestekens. Zo matchen "Vocht/water", "vocht water" en "Vocht-Water".
 */
const normaliseer = (naam: string): string =>
  naam
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Genormaliseerde helpdesklabelnaam → het label zelf. */
const HELPDESK_OP_NAAM: Record<string, TicketLabel> = Object.values(TicketLabel).reduce(
  (map, label) => {
    map[normaliseer(label)] = label;
    return map;
  },
  {} as Record<string, TicketLabel>,
);

/**
 * Renovatiecategorieën die hetzelfde betekenen als een helpdesklabel maar anders
 * geschreven zijn. Enkel toevoegen wanneer het écht dezelfde zaak is — twee
 * verschillende zaken dezelfde kleur geven maakt de lijst juist minder leesbaar.
 */
const SYNONIEMEN: Record<string, TicketLabel> = {
  electriciteit: TicketLabel.Elektriciteit,
  dakwerken: TicketLabel.Dak,
  afbraak: TicketLabel.Afbraakwerken,
  asbestverwijderingofferte: TicketLabel.Asbest,
  ramen: TicketLabel.Buitenschrijnwerk,
  buitenschrijnwerkramen: TicketLabel.Buitenschrijnwerk,
  vloer: TicketLabel.VloerenParket,
  vloeren: TicketLabel.VloerenParket,
  rioleringafwateringenputten: TicketLabel.Riolering,
  nutsvoorzieningenaansluiting: TicketLabel.Nutsvoorzieningen,
  brandveiligheid: TicketLabel.Brandbeveiliging,
  toestellen: TicketLabel.ElectronischeToestellen,
  decoratieenverlichting: TicketLabel.Verlichting,
};

/**
 * Categorieën zonder helpdesktegenhanger krijgen hier hun eigen kleur + icoon.
 * Losstaand van de terugvalkleuren hieronder, zodat de categorieën die het vaakst
 * voorkomen een herkenbaar icoon hebben in plaats van een generiek labeltje.
 */
const EIGEN_STIJL: Record<string, RenoCategoryStyle> = {
  keuken: {
    icon: <Cube className="h-3 w-3" />,
    bgColor: 'bg-orange-200',
    textColor: 'text-orange-900',
  },
  badkamer: {
    icon: <Bathtub className="h-3 w-3" />,
    bgColor: 'bg-cyan-200',
    textColor: 'text-cyan-900',
  },
  gevelisolatie: {
    icon: <Snowflake className="h-3 w-3" />,
    bgColor: 'bg-stone-200',
    textColor: 'text-stone-900',
  },
  isolatie: {
    icon: <Snowflake className="h-3 w-3" />,
    bgColor: 'bg-sky-100',
    textColor: 'text-sky-800',
  },
  schilderwerk: {
    icon: <PaintRoller className="h-3 w-3" />,
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-800',
  },
  schilderwerken: {
    icon: <PaintRoller className="h-3 w-3" />,
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-800',
  },
  verwarming: {
    icon: <ThermometerCold className="h-3 w-3" />,
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800',
  },
};

/**
 * Terugvalpalet — dezelfde tailwind-families als de helpdeskmap, zodat een onbekende
 * categorie er niet uitspringt als "van een ander scherm".
 */
const TERUGVAL: Array<{ bgColor: string; textColor: string }> = [
  { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
  { bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
  { bgColor: 'bg-green-100', textColor: 'text-green-800' },
  { bgColor: 'bg-purple-100', textColor: 'text-purple-800' },
  { bgColor: 'bg-teal-100', textColor: 'text-teal-800' },
  { bgColor: 'bg-rose-100', textColor: 'text-rose-800' },
  { bgColor: 'bg-amber-100', textColor: 'text-amber-800' },
  { bgColor: 'bg-lime-100', textColor: 'text-lime-800' },
  { bgColor: 'bg-indigo-100', textColor: 'text-indigo-800' },
  { bgColor: 'bg-fuchsia-100', textColor: 'text-fuchsia-800' },
  { bgColor: 'bg-emerald-100', textColor: 'text-emerald-800' },
  { bgColor: 'bg-slate-100', textColor: 'text-slate-800' },
  { bgColor: 'bg-zinc-200', textColor: 'text-zinc-900' },
  { bgColor: 'bg-stone-100', textColor: 'text-stone-800' },
];

/**
 * Stabiele index in {@link TERUGVAL} op basis van de naam (djb2-variant). Puur
 * deterministisch: dezelfde categorie geeft altijd dezelfde kleur.
 */
const terugvalIndex = (genormaliseerd: string): number => {
  let hash = 5381;
  for (let i = 0; i < genormaliseerd.length; i += 1) {
    hash = (hash * 33) ^ genormaliseerd.charCodeAt(i);
  }
  return Math.abs(hash) % TERUGVAL.length;
};

/** Generiek icoon voor een categorie zonder eigen icoon, op woordniveau geraden. */
const terugvalIcoon = (genormaliseerd: string): React.ReactNode => {
  if (/water|vocht|riool|riolering|afvoer|put/.test(genormaliseerd)) return <Drop className="h-3 w-3" />;
  if (/elek|elec|stroom/.test(genormaliseerd)) return <Lightning className="h-3 w-3" />;
  if (/lift/.test(genormaliseerd)) return <Elevator className="h-3 w-3" />;
  if (/ventilatie|lucht/.test(genormaliseerd)) return <Compass className="h-3 w-3" />;
  if (/asbest|sloop|afbraak/.test(genormaliseerd)) return <X className="h-3 w-3" />;
  if (/werk|bouw|ruwbouw|gevel|dak|muur/.test(genormaliseerd)) return <Buildings className="h-3 w-3" />;
  if (/onderhoud|herstel|klus/.test(genormaliseerd)) return <Wrench className="h-3 w-3" />;
  if (/reserve|variabel|overige|divers/.test(genormaliseerd)) return <Question className="h-3 w-3" />;
  return <Tag className="h-3 w-3" />;
};

/**
 * Kleur + icoon van één renovatiecategorie. Een lege naam geeft een neutraal labeltje,
 * zodat de aanroeper nooit op `null` hoeft te controleren.
 */
export const getRenoCategoryStyle = (categorie: string): RenoCategoryStyle => {
  const genormaliseerd = normaliseer(categorie ?? '');
  if (!genormaliseerd) {
    return {
      icon: <Tag className="h-3 w-3" />,
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
    };
  }

  const helpdeskLabel = HELPDESK_OP_NAAM[genormaliseerd] ?? SYNONIEMEN[genormaliseerd];
  if (helpdeskLabel && helpdeskLabel !== TicketLabel.UNKNOWN) {
    const { icon, bgColor, textColor } = getLabelIconAndColor(helpdeskLabel);
    return { icon, bgColor, textColor };
  }

  const eigen = EIGEN_STIJL[genormaliseerd];
  if (eigen) return eigen;

  const { bgColor, textColor } = TERUGVAL[terugvalIndex(genormaliseerd)];
  return { icon: terugvalIcoon(genormaliseerd), bgColor, textColor };
};
